import { styled } from "goober";
import { useState } from "react";
import { Sidebar, sidebarRowStyles } from "./ExecutionSidebar";
import { FileChangeDot as ChangeDot } from "./FileChangeDot";
import { FileTypeIcon } from "./FileTypeIcon";
import { FolderIcon } from "./FolderIcon";
import { Icon } from "./Icon";
import { MenuItem } from "./MenuItem";
import { filesShortcut } from "./PinnedPanel";
import { folderChangeIndicator, type MergedFile } from "./stepFilesDiff";
import { Text } from "./Text";

const Row = styled(MenuItem)`
  ${sidebarRowStyles}
  &[data-nested="true"] {
    padding-left: 24px;
  }
`;

const Chevron = styled("span")`
  display: flex;
  margin-left: auto;
  color: var(--text-secondary);
  transition: transform var(--motion-fast) var(--ease-out);

  &[data-expanded="true"] {
    transform: rotate(90deg);
  }
`;

const Reveal = styled("div")`
  display: grid;
  grid-template-rows: 1fr;
  transition:
    grid-template-rows var(--motion-base) var(--ease-out),
    opacity var(--motion-base) var(--ease-out);

  &[data-collapsed="true"] {
    grid-template-rows: 0fr;
    opacity: 0;
  }

  & > div {
    min-height: 0;
    overflow: hidden;
  }

  /* A margin inside the clip, not padding on it, so collapsed rows leave no gap. */
  & > div > * {
    margin-top: 2px;
  }
`;

export type FileGroups = {
  topLevel: MergedFile[];
  folders: [dir: string, files: MergedFile[]][];
};

function FileRow({
  file,
  label,
  nested = false,
  selected,
  onSelect,
}: {
  file: MergedFile;
  label: string;
  nested?: boolean;
  selected: boolean;
  onSelect: (path: string) => void;
}) {
  return (
    <Row
      data-no-pill=""
      data-nested={nested}
      aria-current={selected ? "true" : undefined}
      title={file.path}
      onClick={() => onSelect(file.path)}
    >
      <FileTypeIcon path={file.path} size={16} />
      <Text ellipsis>{label}</Text>
      {file.kind !== "unchanged" && (
        <ChangeDot removed={file.kind === "removed"} />
      )}
    </Row>
  );
}

function FileFolder({
  dir,
  files,
  selectedPath,
  onSelect,
}: {
  dir: string;
  files: MergedFile[];
  selectedPath: string | undefined;
  onSelect: (path: string) => void;
}) {
  const containsSelected = selectedPath?.startsWith(`${dir}/`) ?? false;
  const [expanded, setExpanded] = useState(containsSelected);
  const [prevContainsSelected, setPrevContainsSelected] =
    useState(containsSelected);
  if (containsSelected !== prevContainsSelected) {
    setPrevContainsSelected(containsSelected);
    if (containsSelected) setExpanded(true);
  }
  const indicator = folderChangeIndicator(files);
  return (
    <div role="group" aria-label={dir}>
      <Row
        data-no-pill=""
        aria-expanded={expanded}
        onClick={() => setExpanded(!expanded)}
      >
        <FolderIcon open={expanded} />
        <Text ellipsis>{dir}</Text>
        {!expanded && indicator && (
          <ChangeDot removed={indicator === "removed"} />
        )}
        <Chevron data-expanded={expanded}>
          <Icon name="chevron-right" size={12} />
        </Chevron>
      </Row>
      {files.map((file, index) => (
        <Reveal
          key={file.path}
          data-collapsed={!expanded}
          inert={!expanded}
          style={{ transitionDelay: `${Math.min(index, 10) * 10}ms` }}
        >
          <div>
            <FileRow
              file={file}
              label={file.path.slice(dir.length + 1)}
              nested
              selected={file.path === selectedPath}
              onSelect={onSelect}
            />
          </div>
        </Reveal>
      ))}
    </div>
  );
}

export function StepFilesSidebar({
  groups,
  selectedPath,
  onSelect,
  onUnpin,
}: {
  groups: FileGroups;
  selectedPath: string | undefined;
  onSelect: (path: string) => void;
  onUnpin: () => void;
}) {
  return (
    <Sidebar
      title="Files"
      side="left"
      selectedId={selectedPath}
      collapse={{ shortcut: filesShortcut, onCollapse: onUnpin }}
    >
      {groups.topLevel.map((file) => (
        <FileRow
          key={file.path}
          file={file}
          label={file.path}
          selected={file.path === selectedPath}
          onSelect={onSelect}
        />
      ))}
      {groups.folders.map(([dir, files]) => (
        <FileFolder
          key={dir}
          dir={dir}
          files={files}
          selectedPath={selectedPath}
          onSelect={onSelect}
        />
      ))}
    </Sidebar>
  );
}
